import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import {
  TableColumn,
  TableEmptyState,
  TableFilter,
  TableInspector,
  TablePageAction,
  TablePageActionEvent,
  TablePageComponent,
  TableRowAction,
  TableRowActionEvent,
  TableSummaryItem,
  TableTone
} from 'src/app/con-x/table/table-page.component';
import { UserService } from 'src/app/services/user-service/userservice';
import { AddressBookConfirmDialogComponent } from './address-book-confirm-dialog.component';
import { AddressBookEntryDialogComponent } from './address-book-entry-dialog.component';
import { addressBookErrorMessage } from './address-book-name-dialog.component';
import { AddressBookPrefixSearchComponent } from './address-book-prefix-search.component';
import { AddressBookSearchComponent } from './address-book-search.component';
import { AddressBook, AddressBookEntry } from './address-book.models';
import { AddressBookService } from './address-book.service';

type SearchMode = 'all' | 'fulltext' | 'prefix';
interface SearchRequest { mode: SearchMode; query: string; }
interface SearchResult { entries?: AddressBookEntry[]; error?: unknown; }

@Component({
  selector: 'app-address-book-entries',
  standalone: true,
  imports: [TablePageComponent, MatSnackBarModule, AddressBookSearchComponent, AddressBookPrefixSearchComponent],
  templateUrl: './address-book-entries.component.html',
  styleUrls: ['./address-book-entries.component.scss']
})
export class AddressBookEntriesComponent implements OnInit, OnDestroy {
  @ViewChild(AddressBookSearchComponent) private fullTextSearchComponent?: AddressBookSearchComponent;
  @ViewChild(AddressBookPrefixSearchComponent) private prefixSearchComponent?: AddressBookPrefixSearchComponent;

  addressBookId = 0;
  addressBook: AddressBook | null = null;
  entries: AddressBookEntry[] = [];
  selectedEntry: AddressBookEntry | null = null;
  loading = false;
  errorMessage = '';
  canManage = false;
  private activeSearch: SearchRequest = { mode: 'all', query: '' };
  private readonly searchRequests = new Subject<SearchRequest>();
  private readonly destroyed = new Subject<void>();

  readonly columns: readonly TableColumn<AddressBookEntry>[] = [
    {
      key: 'type', label: 'Typ', type: 'badge', value: row => this.typeLabel(row),
      tone: row => this.typeTone(row), width: '8.5rem'
    },
    { key: 'primaryIdentifier', label: 'Name / Bezeichnung' },
    { key: 'organisation', label: 'Organisation', value: row => this.organisationLabel(row), emptyText: '—' },
    { key: 'email', label: 'E-Mail', value: row => row.emails?.[0], emptyText: '—' },
    { key: 'updatedAt', label: 'Geändert', type: 'datetime' }
  ];

  readonly filters: readonly TableFilter<AddressBookEntry>[] = [{
    id: 'type',
    label: 'Kontakttyp',
    options: [
      { label: 'Personen', value: 'PERSON' },
      { label: 'Organisationen', value: 'ORGANISATION' }
    ],
    allLabel: 'Alle Kontakte'
  }];

  get emptyState(): TableEmptyState {
    if (this.activeSearch.query) {
      return {
        icon: 'search_off',
        title: 'Keine passenden Kontakte',
        description: 'Ändern oder löschen Sie den Suchbegriff.'
      };
    }
    return {
      icon: 'contact_page',
      title: 'Noch keine Kontakte vorhanden',
      description: 'Legen Sie eine Person oder Organisation als ersten Kontakt an.',
      actionId: 'create'
    };
  }

  constructor(
    private readonly addressBookService: AddressBookService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) { }

  get pageActions(): TablePageAction[] {
    const actions: TablePageAction[] = [{ id: 'back', label: 'Adressbücher', icon: 'arrow_back', appearance: 'basic' }];
    if (this.canManage) actions.push({ id: 'create', label: 'Kontakt anlegen', icon: 'person_add' });
    return actions;
  }

  get rowActions(): readonly TableRowAction<AddressBookEntry>[] {
    return [
      { id: 'edit', label: 'Kontakt bearbeiten', icon: 'edit', hidden: () => !this.canManage },
      { id: 'delete', label: 'Kontakt löschen', icon: 'delete', tone: 'warn', hidden: () => !this.canManage }
    ];
  }

  get inspector(): TableInspector<AddressBookEntry> {
    return {
      ariaLabel: 'Kontaktdetails',
      title: 'primaryIdentifier',
      subtitle: row => this.typeLabel(row),
      description: row => this.organisationLabel(row),
      icon: row => row.type === 'PERSON' ? 'person' : 'business',
      tone: row => this.typeTone(row),
      fields: [
        { label: 'Anrede', value: row => row.title, emptyText: '—' },
        { label: 'Adresse', value: row => this.addressLabel(row), emptyText: '—' },
        { label: 'Telefon', value: row => row.phoneNumbers?.map(phone => `${phone.countryCode} ${phone.number}`).join(', '), emptyText: '—' },
        { label: 'E-Mail', value: row => row.emails?.join(', '), emptyText: '—' },
        { label: 'Webseiten', value: row => row.websites?.join(', '), emptyText: '—' },
        { label: 'DIDs', value: row => row.dids?.join(', '), emptyText: '—', monospace: true }
      ],
      actions: this.rowActions
    };
  }

  get summaryItems(): readonly TableSummaryItem<AddressBookEntry>[] {
    return [
      { label: 'Kontakte', value: this.entries.length, icon: 'contacts' },
      { label: 'Personen', value: this.entries.filter(entry => entry.type === 'PERSON').length, icon: 'person' },
      { label: 'Organisationen', value: this.entries.filter(entry => entry.type === 'ORGANISATION').length, icon: 'business' }
    ];
  }

  ngOnInit(): void {
    this.addressBookId = Number(this.route.snapshot.paramMap.get('addressBookId'));
    if (!Number.isInteger(this.addressBookId) || this.addressBookId <= 0) {
      this.errorMessage = 'Die Adressbuch-ID ist ungültig.';
      return;
    }
    const routePath = this.route.snapshot.pathFromRoot ?? [];
    this.canManage = !routePath.some(snapshot => snapshot.paramMap.has('organisationId'));
    this.searchRequests.pipe(
      takeUntil(this.destroyed),
      switchMap(request => {
        this.loading = true;
        this.errorMessage = '';
        return this.searchCall(request).pipe(
          map(entries => ({ entries }) as SearchResult),
          catchError(error => of({ error } as SearchResult))
        );
      })
    ).subscribe(result => {
      this.loading = false;
      if (result.error) {
        this.errorMessage = this.errorText(result.error, 'Die Kontakte konnten nicht geladen werden.');
        this.changeDetectorRef.markForCheck();
        return;
      }
      this.entries = result.entries ?? [];
      this.changeDetectorRef.markForCheck();
    });
    this.loadAddressBook();
    this.refresh();
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  handlePageAction(event: TablePageActionEvent): void {
    if (event.actionId === 'back') void this.router.navigate(['..'], { relativeTo: this.route });
    if (event.actionId === 'create') this.openEntryDialog();
  }

  handleRowAction(event: TableRowActionEvent<AddressBookEntry>): void {
    if (event.actionId === 'edit') this.openEntryDialog(event.row);
    if (event.actionId === 'delete') this.confirmDelete(event.row);
  }

  searchFullText(query: string): void {
    if (query) this.prefixSearchComponent?.reset();
    this.runSearch({ mode: query ? 'fulltext' : 'all', query });
  }

  searchPrefix(query: string): void {
    if (query) this.fullTextSearchComponent?.reset();
    this.runSearch({ mode: query ? 'prefix' : 'all', query });
  }

  refresh(): void {
    this.searchRequests.next(this.activeSearch);
  }

  private runSearch(request: SearchRequest): void {
    this.activeSearch = request;
    this.selectedEntry = null;
    this.searchRequests.next(request);
  }

  private searchCall(request: SearchRequest): Observable<AddressBookEntry[]> {
    if (request.mode === 'fulltext') return this.addressBookService.fullTextSearch(this.addressBookId, request.query);
    if (request.mode === 'prefix') return this.addressBookService.prefixSearch(this.addressBookId, request.query);
    return this.addressBookService.getEntries(this.addressBookId);
  }

  private loadAddressBook(): void {
    this.addressBookService.getAddressBook(this.addressBookId).subscribe({
      next: addressBook => {
        this.addressBook = addressBook;
        this.canManage = addressBook.ownerType === 'USER'
          || this.userService.isCurrentUserPlatformAdmin()
          || this.userService.isUserAdminOfOrganisationOrPlattformAdmin(addressBook.ownerId);
        this.changeDetectorRef.markForCheck();
      },
      error: error => {
        this.errorMessage = this.errorText(error, 'Das Adressbuch konnte nicht geladen werden.');
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  private openEntryDialog(entry?: AddressBookEntry): void {
    this.dialog.open(AddressBookEntryDialogComponent, {
      width: '58rem',
      maxWidth: 'calc(100vw - 2rem)',
      maxHeight: 'calc(100dvh - 2rem)',
      data: { addressBookId: this.addressBookId, entry },
      autoFocus: false
    }).afterClosed().subscribe(result => {
      if (!result) return;
      this.snackBar.open(entry ? 'Kontakt wurde aktualisiert.' : 'Kontakt wurde angelegt.', 'Schließen',
        { duration: 4000 });
      this.refresh();
    });
  }

  private confirmDelete(entry: AddressBookEntry): void {
    this.dialog.open(AddressBookConfirmDialogComponent, {
      width: '31rem',
      maxWidth: 'calc(100vw - 2rem)',
      data: {
        title: 'Kontakt löschen',
        message: 'Dieser Kontakt wird dauerhaft aus dem Adressbuch entfernt.',
        itemName: entry.primaryIdentifier,
        confirmLabel: 'Kontakt löschen'
      }
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.addressBookService.deleteEntry(this.addressBookId, entry.addressBookEntryId).subscribe({
        next: () => {
          this.selectedEntry = null;
          this.entries = this.entries.filter(item => item.addressBookEntryId !== entry.addressBookEntryId);
          this.snackBar.open('Kontakt wurde gelöscht.', 'Schließen', { duration: 4000 });
          this.changeDetectorRef.markForCheck();
        },
        error: error => {
          this.snackBar.open(this.errorText(error, 'Der Kontakt konnte nicht gelöscht werden.'),
            'Schließen', { duration: 6000 });
          this.changeDetectorRef.markForCheck();
        }
      });
    });
  }

  private typeLabel(entry: AddressBookEntry): string {
    return entry.type === 'PERSON' ? 'Person' : 'Organisation';
  }

  private typeTone(entry: AddressBookEntry): TableTone {
    return entry.type === 'PERSON' ? 'primary' : 'accent';
  }

  private organisationLabel(entry: AddressBookEntry): string {
    return entry.type === 'ORGANISATION' ? entry.organisationName ?? '' : entry.organisations?.join(', ') ?? '';
  }

  private addressLabel(entry: AddressBookEntry): string {
    const addresses = entry.type === 'PERSON'
      ? [entry.personAddress]
      : [entry.businessAddress, entry.legalAddress];
    return addresses.filter(Boolean).map(address => {
      const country = this.countryName(address?.countryCode);
      return [address?.street, address?.houseNumber, address?.postalCode, address?.city, address?.state, country]
        .filter(Boolean).join(' ');
    }).join(' · ');
  }

  private countryName(code?: string): string {
    if (!code) return '';
    const DisplayNames = (Intl as any).DisplayNames;
    return DisplayNames ? new DisplayNames(['de'], { type: 'region' }).of(code) ?? code : code;
  }

  private errorText(error: any, fallback: string): string {
    return addressBookErrorMessage(error, fallback);
  }
}
